// ============================================================================
// GROUPING WRAPPER - MAIN COMPONENT
// ============================================================================

import React from "react";
import { BaseEntity } from "@/components/shared/UniversalCRMTable/types/shared";
import { useGroupingWrapperData } from "./useGroupingWrapperData";
import { useGroupingState } from "./useGroupingState";
import { useGroupingPageContext } from "./useGroupingPageContext";
import { GroupingWrapperContent } from "./GroupingWrapperContent";
import type { GroupingWrapperProps } from "./types";

/**
 * GroupingWrapper - HOC that manages all grouping logic
 */
export function GroupingWrapper<T extends BaseEntity>(props: GroupingWrapperProps<T>) {
  const wrappedData = useGroupingWrapperData(props);

  const finalData = wrappedData.data || [];
  const finalConfig = wrappedData.config;

  if (!finalConfig) {
    console.error("GroupingWrapper: config is required (either from context or props)");
    return null;
  }

  const {
    groupableFields,
    processedData,
    groupingContext,
    groupByField,
    dualGrouping,
  } = useGroupingState({ data: finalData, config: finalConfig });

  const { viewMode, appUser, BoardViewComponent } = useGroupingPageContext();

  return (
    <GroupingWrapperContent
      viewMode={viewMode}
      BoardViewComponent={BoardViewComponent}
      appUser={appUser}
      data={finalData}
      config={finalConfig}
      processedData={processedData}
      groupingContext={groupingContext}
      groupableFields={groupableFields}
      groupByField={groupByField}
      dualGrouping={dualGrouping}
      searchTerm={wrappedData.searchTerm ?? ""}
      setSearchTerm={wrappedData.setSearchTerm ?? (() => {})}
      filters={wrappedData.filters ?? {}}
      setFilters={wrappedData.setFilters ?? (() => {})}
      onExport={wrappedData.onExport ?? (() => {})}
      onEdit={wrappedData.onEdit ?? (() => {})}
      onView={wrappedData.onView ?? (() => {})}
      onOpenDetailModal={wrappedData.onOpenDetailModal}
      hooks={wrappedData.hooks}
      hasNextPage={wrappedData.hasNextPage ?? false}
      isFetchingNextPage={wrappedData.isFetchingNextPage ?? false}
      onLoadMore={wrappedData.onLoadMore}
      children={props.children}
    />
  );
}
